import { Navigate, Outlet } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/app/providers/AuthProvider';
import { signOut } from '@/features/auth';
import { Button } from '@/shared/components/ui/button';
import { LoadingState } from '@/shared/components/LoadingState';
import { messages } from '@/shared/constants/messages';

/**
 * Guarda de rota — conveniência de UX (architecture.md §5.3): uma URL
 * acessada direto por quem não tem permissão volta vazia pela RLS de
 * qualquer forma. O que este componente evita é a tela quebrada, não o
 * vazamento de dado.
 */
export function RequireAuth() {
  const { session, isSessionLoading, currentUser, currentUserError } = useAuth();
  const queryClient = useQueryClient();

  // Computador compartilhado (secretaria): não deixar cache da pessoa
  // anterior visível para quem logar em seguida na mesma aba.
  async function handleSignOut() {
    await signOut();
    queryClient.clear();
  }

  if (isSessionLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingState label="Verificando sessão…" />
      </div>
    );
  }

  if (!session) return <Navigate to="/login" replace />;

  // Falha na busca também deixa `currentUser` em undefined. Sem este ramo, um
  // erro vira spinner permanente — a pessoa fica olhando "Carregando…" para
  // sempre, sem nada a fazer e sem sinal de que algo quebrou.
  if (currentUserError) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="w-full max-w-sm space-y-4 text-center">
          <p className="text-sm">{messages.errors.unexpected}</p>
          <Button variant="outline" onClick={() => void handleSignOut()}>
            Sair
          </Button>
        </div>
      </div>
    );
  }

  // undefined: ainda buscando pessoa + papéis. null: sem vínculo vigente.
  if (currentUser === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingState label="Carregando seus dados…" />
      </div>
    );
  }

  if (currentUser === null) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="w-full max-w-sm space-y-4 text-center">
          <p className="text-sm">{messages.auth.noAccess}</p>
          <Button variant="outline" onClick={() => void handleSignOut()}>
            Sair
          </Button>
        </div>
      </div>
    );
  }

  return <Outlet />;
}
